import { useTranslation } from "react-i18next";
import Icon from "@/src/lib/components/custom/Icon";
import { Image } from "@/src/lib/components/custom/Image";
import { Badge } from "@/src/lib/components/ui/badge";
import { Button } from "@/src/lib/components/ui/button";
import { Skeleton } from "@/src/lib/components/ui/skeleton";
import type { GetTypes } from "@/src/lib/types/api-types";
import { cn } from "@/src/lib/utils";

interface AdminOrderCardProps {
	orderId?: number;
	initialOrderData: GetTypes["orders"]["getAll"] extends {
		orders?: infer Orders;
	}
		? Orders extends (infer Order)[]
			? Order
			: never
		: never;
	onClick?: () => void;
	getStatusColor: (status: string, paymentStatus?: string) => string;
	getStatusText: (status: string, paymentStatus?: string) => string;
	formatDateTime: (date: string) => string;
}

export default function AdminOrderCard({
	initialOrderData,
	onClick,
	getStatusColor,
	getStatusText,
	formatDateTime,
}: AdminOrderCardProps) {
	const order = initialOrderData;
	const items = order.items || [];
	const hasFullDetails = items.length > 0;
	const isLoading = false; // No longer loading per-card
	const { t } = useTranslation();

	const displayOrder = order;

	// Parse scheduled date safely
	const scheduledDate = displayOrder.scheduledFor
		? (() => {
				try {
					const date = new Date(displayOrder.scheduledFor);
					return isNaN(date.getTime()) ? null : date;
				} catch {
					return null;
				}
			})()
		: null;

	return (
		<div className="p-5 flex flex-col h-full" onClick={onClick}>
			<div className="flex justify-between items-start mb-4">
				<div className="flex flex-col">
					<span className="font-mono text-xs text-muted-foreground mb-1">
						#{displayOrder.id}
					</span>
					<h3
						className="font-semibold text-base truncate max-w-[150px]"
						title={displayOrder.user?.name ?? "Unknown Customer"}
					>
						{displayOrder.user?.name ?? "Unknown Customer"}
					</h3>
				</div>
				<div className="flex flex-col gap-1 items-end">
					{scheduledDate && (
						<Badge
							variant="outline"
							className="text-xs font-medium px-2 py-0.5 mb-1 bg-secondary/10 text-secondary border-secondary/20"
						>
							<Icon name="Clock" className="w-3 h-3 mr-1" />
							{scheduledDate.toLocaleTimeString([], {
								hour: "numeric",
								minute: "2-digit",
								hour12: true,
							})}
						</Badge>
					)}
					<Badge
						className={cn(
							"text-xs font-medium px-2.5 py-1",
							getStatusColor(
								displayOrder.status,
								displayOrder.paymentStatus ?? undefined,
							),
						)}
					>
						{getStatusText(
							displayOrder.status,
							displayOrder.paymentStatus ?? undefined,
						)}
					</Badge>
				</div>
			</div>

			{/* Order Items Preview */}
			<div className="flex-1 mb-4 space-y-3">
				<div className="flex items-center gap-2 text-sm text-muted-foreground bg-gray-50 p-2 rounded-lg">
					<Icon name="MapPin" className="w-4 h-4 shrink-0 text-primary/70" />
					<span className="truncate">
						{displayOrder.address?.streetAddress}, {displayOrder.address?.city}
					</span>
				</div>

				<div className="space-y-1">
					<p className="text-xs font-medium text-muted-foreground uppercase tracking-wider mb-2">
						{t("admin.order_card.order_items")}
					</p>
					<div className="flex gap-2 overflow-hidden">
						{isLoading && !hasFullDetails ? (
							// Loading skeletons for items
							Array.from({ length: 3 }).map((_, i) => (
								<Skeleton key={i} className="w-10 h-10 rounded-md" />
							))
						) : items.length > 0 ? (
							<>
								{items.slice(0, 3).map((item, i: number) => (
									<div
										key={i}
										className="relative w-10 h-10 rounded-md bg-gray-100 border overflow-hidden shrink-0 flex items-center justify-center"
									>
										{item.menuItem?.imageUrl ? (
											<Image
												src={item.menuItem.imageUrl}
												alt=""
												className="w-full h-full object-cover"
											/>
										) : (
											<Icon name="Utensils" className="w-4 h-4 text-gray-400" />
										)}
									</div>
								))}
								{items.length > 3 && (
									<div className="w-10 h-10 rounded-md bg-gray-100 border flex items-center justify-center text-xs font-medium text-muted-foreground">
										+{items.length - 3}
									</div>
								)}
							</>
						) : (
							<div className="text-xs text-muted-foreground italic">
								{t("admin.order_card.no_items")}
							</div>
						)}
					</div>
				</div>
			</div>

			<div className="pt-4 mt-auto border-t flex items-center justify-between">
				<div className="flex flex-col">
					<span className="text-xs text-muted-foreground">
						{t("admin.order_card.total_amount")}
					</span>
					<span className="text-lg font-bold text-primary">
						€{Number(displayOrder.totalPrice).toFixed(2)}
					</span>
					<span
						className={`text-xs font-medium ${displayOrder.paymentMethod === "cash" ? "text-amber-600" : "text-blue-600"}`}
					>
						{displayOrder.paymentMethod === "cash"
							? "Cash on Delivery"
							: "Card"}
					</span>
				</div>
				<div className="flex flex-col items-end text-right">
					<span className="text-xs text-muted-foreground flex items-center gap-1">
						<Icon name="Clock" className="w-3 h-3" />
						{formatDateTime(displayOrder.createdAt)}
					</span>
					<Button
						size="sm"
						variant="ghost"
						className="h-8 pr-0 hover:bg-transparent hover:text-primary"
					>
						{t("admin.order_card.details")}{" "}
						<Icon name="ArrowRight" className="w-4 h-4 ml-1" />
					</Button>
				</div>
			</div>
		</div>
	);
}
